import { useEffect, useRef, useCallback, useState, useMemo } from 'react'

import { useDecision } from '@optimizely/react-sdk'
import { ThemeMode } from '@space-core/styles/themes/enum'
import useThemeContext from '@space-shell/common/hooks/useThemeContext'
import type { PurchasedData } from '@space-shell/common/services/product/types'
import { useAuthStore } from '@space-shell/core/store'
import { lazyRemoteModule } from '@space-shell/lib/runtime'
import { useTranslation } from 'react-i18next'

import { REOPEN_INTERVAL_IN_MS } from './constants'

type DelayedPurchaseChargeModalProps = {
  container: React.RefObject<HTMLDivElement>
  activePurchase: PurchasedData
}

export const DelayedPurchaseChargeModal = ({
  container,
  activePurchase
}: DelayedPurchaseChargeModalProps) => {
  const { i18n } = useTranslation()
  const modalRenderState = useRef(false)
  const reopenIntervalId = useRef<number>()
  const { theme } = useThemeContext()
  const { email, id, token, ucode } = useAuthStore()
  const [isOpen, setIsOpen] = useState(false)
  const [shouldRenderPurchaseChargeModalFlag] = useDecision('should_render_purchase_charge_modal')

  const clearReopenInterval = useCallback(() => {
    if (reopenIntervalId.current) {
      window.clearInterval(reopenIntervalId.current)
      reopenIntervalId.current = undefined
    }
  }, [])

  const setupReopenInterval = useCallback(() => {
    clearReopenInterval()

    reopenIntervalId.current = window.setInterval(() => {
      modalRenderState.current = false
      setIsOpen(true)
    }, REOPEN_INTERVAL_IN_MS)
  }, [clearReopenInterval])

  const userProps = useMemo(
    () => ({
      email,
      id: Number(id),
      language: i18n.language,
      token,
      ucode
    }),
    [email, id, i18n.language, token, ucode]
  )

  useEffect(() => {
    async function importModule() {
      const remoteModule = await lazyRemoteModule<(args?: object) => Promise<void>>(
        'collections/renderDelayedPurchaseChargeModal'
      )

      const rulesToRenderChargeModal =
        shouldRenderPurchaseChargeModalFlag.enabled &&
        !modalRenderState.current &&
        container.current &&
        activePurchase &&
        remoteModule

      if (!rulesToRenderChargeModal) {
        return
      }

      modalRenderState.current = true

      remoteModule({
        container: container.current,
        props: {
          appOrigin: 'club_classroom',
          purchaseData: activePurchase,
          type: 'DELAYED',
          isOpen: true,
          onClose: () => {
            setIsOpen(false)
            modalRenderState.current = false
            setupReopenInterval()
          },
          user: userProps,
          isDarkMode: theme === ThemeMode.DARK
        }
      }).catch(console.error)
    }

    importModule()

    return clearReopenInterval
  }, [
    activePurchase,
    clearReopenInterval,
    container,
    isOpen,
    setupReopenInterval,
    shouldRenderPurchaseChargeModalFlag.enabled,
    theme,
    userProps
  ])

  return null
}

export default DelayedPurchaseChargeModal
